import type { GetDatasourceQuery } from '@/lib/graphql/generated/graphql';
import { toast } from '@/hooks/use-toast';
import type { TFunction } from 'i18next';

/** Durée par défaut du toast de succès (identique à Radix). Seuls les "en cours" sont infinis. */
const SUCCESS_TOAST_DURATION_MS = 5000;
const GENERIC_DATASOURCE_KIND = 'datasourceProcessed';

type DealDataSource = NonNullable<
  NonNullable<GetDatasourceQuery['deal']>['dataSources'][number]
>;
type DealItemsToastKind = typeof GENERIC_DATASOURCE_KIND | 'itemsCreated';

/** Toast content plus optional variant (e.g. default for neutral "no match" outcome). */
export type DatasourceProcessedToastContent = {
  title: string;
  description: string;
  variant?: 'default' | 'destructive';
};

/**
 * Builds toast content from a processed datasource (GetDatasource result).
 * Uses importSummary for accurate, detailed messages. No-match case returns
 * neutral title/variant so it is not presented as a success.
 */
export function getDatasourceProcessedToastContent(
  datasource: DealDataSource | null | undefined,
  t: TFunction
): DatasourceProcessedToastContent {
  const successTitle = t('general.success');
  const neutralTitle = t(
    'components.deal_items_subscription.toast_datasource_completed'
  );

  if (!datasource) {
    return {
      title: successTitle,
      description: t(
        'components.deal_items_subscription.toast_datasource_completed'
      ),
    };
  }

  const summary =
    'importSummary' in datasource && datasource.importSummary
      ? datasource.importSummary
      : null;

  if (!summary) {
    return {
      title: successTitle,
      description: t(
        'components.deal_items_subscription.toast_datasource_completed'
      ),
    };
  }

  const created = summary.itemsCreatedIds.length;
  const selectedUpdated = summary.selectedItemsUpdatedIds.length;
  const excludedUpdated = summary.excludedItemsUpdatedIds.length;
  const skipped = summary.itemsSkippedIds.length;
  const hasUpdates = selectedUpdated > 0 || excludedUpdated > 0;

  // Created: precise count; optionally add update/excluded lines
  if (created > 0) {
    const createdKey =
      created === 1
        ? 'components.deal_items_subscription.toast_items_created_one'
        : 'components.deal_items_subscription.toast_items_created_other';
    let description = t(createdKey, { count: created });
    if (selectedUpdated > 0) {
      const selKey =
        selectedUpdated === 1
          ? 'components.deal_items_subscription.toast_updated_selection_one'
          : 'components.deal_items_subscription.toast_updated_selection_other';
      description += ` ${t(selKey, { count: selectedUpdated })}`;
    }
    if (excludedUpdated > 0) {
      const exclKey =
        excludedUpdated === 1
          ? 'components.deal_items_subscription.toast_updated_excluded_one'
          : 'components.deal_items_subscription.toast_updated_excluded_other';
      description += ` ${t(exclKey, { count: excludedUpdated })}`;
    }
    return { title: successTitle, description };
  }

  // Updated (selection and/or excluded): precise counts + optional excluded line
  if (hasUpdates) {
    const exclKey =
      excludedUpdated === 1
        ? 'components.deal_items_subscription.toast_updated_excluded_one'
        : 'components.deal_items_subscription.toast_updated_excluded_other';
    let description: string;
    if (selectedUpdated > 0) {
      const selKey =
        selectedUpdated === 1
          ? 'components.deal_items_subscription.toast_updated_selection_one'
          : 'components.deal_items_subscription.toast_updated_selection_other';
      description = t(selKey, { count: selectedUpdated });
      if (excludedUpdated > 0) {
        description += ` ${t(exclKey, { count: excludedUpdated })}`;
      }
    } else {
      description = t(exclKey, { count: excludedUpdated });
    }
    return { title: successTitle, description };
  }

  // No match: neutral toast (not a success)
  const noMatchKey =
    skipped > 0
      ? 'components.deal_items_subscription.toast_datasource_no_match_with_skipped'
      : 'components.deal_items_subscription.toast_datasource_no_match';
  return {
    title: neutralTitle,
    description: t(noMatchKey, { count: skipped }),
    variant: 'default',
  };
}

/** toast().update accepts ToasterToast fields (id added by reducer); we pass title, description, duration?, variant?. */
type ToastUpdate = (p: {
  title?: unknown;
  description?: unknown;
  duration?: number;
  variant?: 'default' | 'destructive';
}) => void;
type ToastDismiss = () => void;

export type { ToastUpdate };

interface TrackedToastRef {
  trackerId: string;
  kind: DealItemsToastKind;
  datasourceId?: string;
  createdItemId?: string;
  update: ToastUpdate;
  dismiss: ToastDismiss;
}

const trackedToasts = new Map<string, TrackedToastRef>();
const datasourceToastIds = new Map<string, string>();
const createdItemToastIds = new Map<string, string>();
let toastTrackerCount = 0;

function getNextToastTrackerId(): string {
  toastTrackerCount = (toastTrackerCount + 1) % Number.MAX_SAFE_INTEGER;
  return `deal-items-toast-${toastTrackerCount}`;
}

/**
 * Les `datasourceId` pour lesquels un toast "en cours" attend encore sa
 * résolution, dans cet onglet.
 *
 * Permet au canal SSE de déduire *quel* import vient d'aboutir sans que le
 * signal ait à transporter l'identifiant : le client attend les datasources
 * qu'il a lui-même lancés. La liste se vide d'elle-même à mesure que les toasts
 * sont résolus (`unregisterTrackedToast`), ce qui rend la réconciliation
 * idempotente et donc rejouable à chaque reconnexion.
 */
export function pendingDatasourceToastIds(): string[] {
  return Array.from(datasourceToastIds.keys());
}

function unregisterTrackedToast(
  trackerId: string
): TrackedToastRef | undefined {
  const trackedToast = trackedToasts.get(trackerId);
  if (!trackedToast) return undefined;

  trackedToasts.delete(trackerId);
  if (trackedToast.datasourceId) {
    datasourceToastIds.delete(trackedToast.datasourceId);
  }
  if (trackedToast.createdItemId) {
    createdItemToastIds.delete(trackedToast.createdItemId);
  }
  return trackedToast;
}

/**
 * Les `dealItemId` d'items créés pour lesquels un toast "en cours" attend encore
 * sa résolution, dans cet onglet.
 *
 * Même rôle que {@link pendingDatasourceToastIds} pour la création d'items : le
 * signal SSE annonce qu'il y a eu création, sans dire laquelle. Résoudre par
 * `kind` seul ne fonctionnerait pas, car `resolveTrackedToastId` n'accepte ce
 * repli que pour un tracker non lié — et l'appelant lie toujours l'item créé dès
 * que la mutation aboutit.
 */
export function pendingCreatedItemToastIds(): string[] {
  return Array.from(createdItemToastIds.keys());
}

/**
 * Toast "en cours" réservé par {@link claimDealItemsInProgressToast} : déjà sorti
 * du registre, mais encore résoluble par son détenteur.
 */
export type ClaimedDealItemsToast = {
  resolveWithSuccess: (content: DatasourceProcessedToastContent) => void;
  dismiss: () => void;
};

/**
 * Réserve le toast "en cours" correspondant à la portée demandée : le désenregistre
 * immédiatement et rend de quoi le résoudre plus tard.
 *
 * Pensé pour une résolution **asynchrone**. `replaceDealItemsInProgressWithSuccess`
 * et `clearDealItemsInProgressToast` décident du sort du toast au moment où elles
 * le retrouvent ; un appelant qui doit d'abord attendre une requête n'a que deux
 * mauvaises options : garder le toast enregistré pendant l'attente — et un second
 * signal le résoudrait une deuxième fois — ou l'effacer d'avance, et il n'a alors
 * plus rien à mettre à jour quand la réponse arrive.
 *
 * La réservation coupe le nœud : l'identifiant sort de `pendingDatasourceToastIds`
 * dès l'appel, donc un signal rejoué (`SYNC` de reconnexion) ne réserve rien et
 * n'a rien à afficher, tandis que la référence rendue permet encore de choisir
 * entre `resolveWithSuccess` et `dismiss` (le résultat s'affiche ailleurs, par
 * exemple dans le rapport d'import).
 */
export function claimDealItemsInProgressToast(options: {
  datasourceId?: string | null;
  createdItemId?: string | null;
  kind?: DealItemsToastKind;
}): ClaimedDealItemsToast | undefined {
  const trackerId = resolveTrackedToastId(options);
  if (!trackerId) return undefined;

  const trackedToast = unregisterTrackedToast(trackerId);
  if (!trackedToast) return undefined;

  return {
    resolveWithSuccess: (content) =>
      trackedToast.update({ ...content, duration: SUCCESS_TOAST_DURATION_MS }),
    dismiss: () => trackedToast.dismiss(),
  };
}

function resolveTrackedToastId(options?: {
  datasourceId?: string | null;
  createdItemId?: string | null;
  kind?: DealItemsToastKind;
}): string | undefined {
  if (options?.datasourceId) {
    const trackerId = datasourceToastIds.get(options.datasourceId);
    if (trackerId) {
      return trackerId;
    }
  }
  if (options?.createdItemId) {
    const trackerId = createdItemToastIds.get(options.createdItemId);
    if (trackerId) {
      return trackerId;
    }
  }
  if (options?.kind) {
    const pendingTrackedToast = Array.from(trackedToasts.values()).find(
      (trackedToast) =>
        trackedToast.kind === options.kind &&
        !trackedToast.datasourceId &&
        !trackedToast.createdItemId
    );
    return pendingTrackedToast?.trackerId;
  }
  return undefined;
}

/**
 * Enregistre le toast "en cours" pour une action dont le succès sera notifié
 * par la subscription dealItemsUpdatedV3. replaceDealItemsInProgressWithSuccess
 * remplacera ce toast par le message de succès.
 */
export function registerDealItemsInProgressToast(
  update: ToastUpdate,
  dismiss: ToastDismiss,
  options?: {
    kind?: DealItemsToastKind;
  }
): string {
  const trackerId = getNextToastTrackerId();
  const kind = options?.kind ?? GENERIC_DATASOURCE_KIND;
  trackedToasts.set(trackerId, {
    trackerId,
    kind,
    update,
    dismiss,
  });
  return trackerId;
}

export function bindDealItemsInProgressToastToDatasource(
  trackerId: string,
  datasourceId: string | null | undefined
): void {
  if (!datasourceId) return;

  const trackedToast = trackedToasts.get(trackerId);
  if (!trackedToast) return;

  const previousTrackerId = datasourceToastIds.get(datasourceId);
  if (previousTrackerId && previousTrackerId !== trackerId) {
    unregisterTrackedToast(previousTrackerId)?.dismiss();
  }

  trackedToast.datasourceId = datasourceId;
  datasourceToastIds.set(datasourceId, trackerId);
}

export function bindDealItemsInProgressToastToCreatedItem(
  trackerId: string,
  createdItemId: string | null | undefined
): void {
  if (!createdItemId) return;

  const trackedToast = trackedToasts.get(trackerId);
  if (!trackedToast) return;

  const previousTrackerId = createdItemToastIds.get(createdItemId);
  if (previousTrackerId && previousTrackerId !== trackerId) {
    unregisterTrackedToast(previousTrackerId)?.dismiss();
  }

  trackedToast.createdItemId = createdItemId;
  createdItemToastIds.set(createdItemId, trackerId);
}

/**
 * Remplace le toast "en cours" par le toast final (succès ou neutre, durée 5s),
 * ou affiche un nouveau toast si aucun "en cours" n'était enregistré.
 * Pour un résultat "no match", le contenu peut inclure variant: 'default' et un titre neutre.
 */
export function replaceDealItemsInProgressWithSuccess(
  content: {
    title: string;
    description: string;
    variant?: 'default' | 'destructive';
  },
  options?: {
    datasourceId?: string | null;
    createdItemId?: string | null;
    kind?: DealItemsToastKind;
    fallbackToToast?: boolean;
  }
): void {
  const trackerId = resolveTrackedToastId(options);
  const payload = {
    ...content,
    duration: SUCCESS_TOAST_DURATION_MS,
  };
  if (trackerId) {
    const trackedToast = unregisterTrackedToast(trackerId);
    trackedToast?.update(payload);
  } else if (options?.fallbackToToast !== false) {
    toast(payload);
  }
}

/**
 * Supprime le toast "en cours" et nettoie la référence (à appeler en cas d'erreur
 * de la mutation, pour que la subscription ne remplace pas par erreur).
 */
export function clearDealItemsInProgressToast(options?: {
  trackerId?: string;
  datasourceId?: string | null;
  createdItemId?: string | null;
  kind?: DealItemsToastKind;
  clearAll?: true;
}): void {
  if (!options) {
    if (import.meta.env.DEV) {
      console.warn(
        'clearDealItemsInProgressToast() called without options. Pass a tracker scope or { clearAll: true }.'
      );
    }
    return;
  }

  if (options.clearAll) {
    Array.from(trackedToasts.keys()).forEach((trackedToastId) => {
      unregisterTrackedToast(trackedToastId)?.dismiss();
    });
    return;
  }

  if (options?.trackerId) {
    unregisterTrackedToast(options.trackerId)?.dismiss();
    return;
  }

  const trackerId = resolveTrackedToastId(options);
  if (trackerId) {
    unregisterTrackedToast(trackerId)?.dismiss();
    return;
  }
}
